import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  AfterViewInit,
} from '@angular/core';
import { select, Store } from '@ngrx/store';
import * as loginSelectors from '../../../root-store/login-store/login.selector';
import { fromEvent, Observable, Subscription } from 'rxjs';
import { MatSidenav } from '@angular/material/sidenav';
import { SetupItemCardNavService } from '@app/shared/services/setup-item-card-nav/setup-item-card-nav.service';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { loginStoreActions } from '@app/root-store';
import { ToastrService } from 'ngx-toastr';
import { AuthService, UserService } from '@app/shared/services';
import { EventNames, EVENTS } from '@app/constants/events.constants';
import { ChatWidgetService } from '@app/shared/services/chat-widget/chat-widget.service';
import { AutoUnsubscribe } from '@app/shared/decorators/auto-unsubscribe.decorator';
import { CommonModule } from '@angular/common';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AccountSecurityComponent } from '@app/modules/setting/components/account-security/account-security.component';
import { PremiumKeysComponent } from './premium-keys/premium-keys.component';
import { InstructionComponent } from '../network-setup/components/instruction-component/instruction.component';
import { ButtonSlideComponent } from '../button-slide/button-slide.component';
@AutoUnsubscribe()
@Component({
    selector: 'security-privacy-setup',
    templateUrl: './security-privacy-setup.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
      CommonModule,
      FormsModule,
      ReactiveFormsModule,
      MatTooltipModule,
      MatSlideToggleModule,
      MatDividerModule,
      MatProgressSpinnerModule,
      InstructionComponent,
      AccountSecurityComponent,
      PremiumKeysComponent,
      ButtonSlideComponent
    ]
})
export class SecurityPrivacySetupComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() accountType;
  @ViewChild('sidenav') public sidenav: MatSidenav;
  @Output() closeAccountSetupSideBarEventEmitter = new EventEmitter();

  user$;
  showLoader = false;
  screenWidth: number;
  switchFromUser = false;
  subscription: Subscription = new Subscription();

  steps: Array<{
    title: string;
    completed: boolean;
    status: 'inactive' | 'active';
    step?: number;
    disabled?: boolean;
  }> = [
    { title: 'instructions', completed: false, status: 'inactive', step: 0 },
    {
      title: 'Two Factor Authentication',
      completed: false,
      status: 'inactive',
      step: 1,
      disabled: false,
    },
    // {
    //   title: 'Search Visibility',
    //   completed: false,
    //   status: 'inactive',
    //   step: 2,
    // },
    {
      title: 'Premium Keys',
      completed: false,
      status: 'inactive',
      step: 2,
    },
    {
      title: 'Status',
      completed: false,
      status: 'inactive',
      step: 3,
    },
  ];

  @Input() isImpersonating = false;
  resizeObservable$: Observable<Event>;
  currentActiveIndex = 0;
  currentActiveElement;
  currentUser;
  markAsComplete = false;
  setupInstructions = [];
  loginStateObject: any;
  get name_search_enabled() {
    return this.searchVisibilityForm.controls['name_search_enabled'].value;
  }
  get email_search_enabled() {
    return this.searchVisibilityForm.controls['email_search_enabled'].value;
  }
  constructor(
    private store: Store,
    private cd: ChangeDetectorRef,
    private setupItemCardNavService: SetupItemCardNavService,
    private userService: UserService,
    private toaster: ToastrService,
    private fb: UntypedFormBuilder,
    private chatWidgetService: ChatWidgetService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.searchVisibilityForm = this.fb.group({
      name_search_enabled: [false],
      email_search_enabled: [false],
    });
    this.screenWidth = window.innerWidth;
    this.resizeObservable$ = fromEvent(window, 'resize');
    this.subscription.add(
      this.resizeObservable$.subscribe((evt) => {
        this.screenWidth = evt.target['innerWidth'];
      })
    );
    this.subscription.add(
      this.store.pipe(select(loginSelectors.getCurrentUserFullDetails)).subscribe(async (result) => {
        try {
          this.loginStateObject = { ...result };

          // if (result) {
          //   this.isImpersonating = true;
          // }
          const authUser = this.authService.user;
          if (result?.switch_from_user_id && result?.id != authUser['id']) {
            this.isImpersonating = true;
            this.steps = this.steps.map((step) => {
              if (step.title === 'Two Factor Authentication') {
                return {
                  ...step,
                  disabled: true,
                };
              }
              return step;
            });
          } else {
            this.isImpersonating = false;
            this.steps = this.steps.map((step) => {
              if (step.title === 'Two Factor Authentication') {
                return {
                  ...step,
                  disabled: false,
                };
              }
              return step;
            });
          }

          this.searchVisibilityForm.controls['name_search_enabled'].setValue(this.loginStateObject.name_search_enabled);
          this.searchVisibilityForm.controls['email_search_enabled'].setValue(this.loginStateObject.email_search_enabled);

          this.cd.detectChanges();
        } catch (ex) {}
      })
    );
    this.user$ = this.store.select(loginSelectors.getCurrentUserDetailsAll);
    this.subscription.add(
      this.user$.subscribe((user) => {
        this.currentUser = user;
        this.cd.detectChanges();
      })
    );
    this.setupInstructions = [
      {
        image: '/assets/images/two-factor-auth-1.svg',
        title: 'Why do I need two factor authentication?',
        list: [
          `2FA adds an additional step beyond just a password, making it much harder for bad actors to gain access to your account. Even if someone steals or guesses your password, they still need the second factor (a code sent to your phone).`,
        ],
      },
      {
        image: '/assets/images/search-visibility.svg',

        title: 'What is search visibility?',
        list: [
          'If you need to share data with another Kinnect user, or another Kinnect user needs to share data with you, you will need to be connected to them in order to do so. In order to connect with another user, you will need to be able to search for them to send a connection request.',
          'We understand you might not want to be searchable to everyone. You have the ability to turn your visibility on and off, so you might need to adjust these settings when you want to connect with other active Kinnect users.',
        ],
      },
      {
        image: '/assets/images/premium-encryption-key-1.svg',

        title: 'What are premium encryption keys?',
        list: [
          'Kinnect empowers users to create and assign asymmetric encryption keys to protect individual files at their own discretion.',
          'To learn more about how asymmetric encryption works, we recommend reading this article in our help center.',
        ],
      },
      {
        image: '/assets/images/security-icon-2.svg',

        title: 'How can I learn more about Kinnect security?',
        list: [
          'We recommend reading <a href="https://help.kinnect.us/category/15619-privacy-security" target="_blank">this</a> article in our help center.',
        ],
      },
    ];
  }

  ngAfterViewInit() {
    this.steps[0].completed = false;
    this.setStatusForSidenavMenu(0);
    this.trackEventsInMixPanel([{ eventName: EVENTS.security_privacy_instruction_start }]);
  }

  closeSideNav() {
    // let events = [{ eventName: EVENTS.security_privacy_setup_close }];
    // if (this.markAsComplete) events.push({ eventName: EVENTS.security_privacy_status_complete });
    // this.trackEventsInMixPanel(events);
    this.setupItemCardNavService.handleNav(false, 'security_and_privacy');
    this.closeAccountSetupSideBarEventEmitter.emit(false);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.userService.cleanupWorker();
    this.markAsComplete = false;
  }
  handleSidenavMenuNavigation(index?: number) {
    if (!!index || index == 0) {
      this.setStatusForSidenavMenu(index);
    } else {
      this.setStatusForSidenavMenu();
    }
  }

  trackEventsInMixPanel(events: { eventName: EventNames; data?: any }[]) {
    this.userService.trackEventsInMixPanel(events).then((response) => {});
  }

  setStatusForSidenavMenu(activeIndex?: number) {
    try {
      if (activeIndex === undefined) {
        //   // Move to the next step
        if (this.currentActiveIndex !== undefined && this.currentActiveIndex < this.steps.length - 1) {
          // if (this.currentActiveIndex === 2) {
          //   activeIndex = this.currentActiveIndex + 1;
          // if (this.currentActiveIndex === 3 && this.currentViewOnStep3 === 'about') {
          //   activeIndex = this.currentActiveIndex + 1;
          //   this.currentViewOnStep3 = 'premium-keys';
          // } else if (this.currentActiveIndex === 3 && this.currentViewOnStep3 === 'premium-keys') {
          //   activeIndex = 4;
          // this.currentViewOnStep3 = 'premium-keys';
          // } else if (this.currentActiveIndex === 3 && this.currentViewOnStep3 === 'premium-keys') {
          // activeIndex = 4;
          // } else {
          if (this.steps[this.currentActiveIndex + 1].disabled) {
            activeIndex = this.currentActiveIndex + 2;
          } else {
            activeIndex = this.currentActiveIndex + 1;
            // }
          }
        } else {
          // If no more steps are available, do nothing or reset to the first step
          activeIndex = 0; // or handle this case based on your requirements
        }
      }

      if (activeIndex !== -1 && activeIndex < this.steps.length) {
        for (let i = 0; i < this.steps.length; i++) {
          if (i === activeIndex) {
            this.steps[i].status = 'active';
            this.currentActiveElement = this.steps[i];
          } else {
            this.steps[i].status = 'inactive';
          }
        }
        this.markAsComplete = false;
        this.currentActiveIndex = activeIndex;
        this.cd.detectChanges();
      }

      if (this.currentActiveIndex == 1) {
        this.trackEventsInMixPanel([{ eventName: EVENTS.security_privacy_instruction_end }, { eventName: EVENTS.security_privacy_2fa_start }]);
      } else if (this.currentActiveIndex == 2) {
        this.trackEventsInMixPanel([{ eventName: EVENTS.security_privacy_2fa_end }, { eventName: EVENTS.security_privacy_search_visibility_start }]);
      }
      //  else if (this.currentActiveIndex == 3) {
      //   this.trackEventsInMixPanel([
      //     { eventName: EVENTS.security_privacy_search_visibility_end },
      //     { eventName: EVENTS.security_privacy_premium_keys_start },
      //   ]);
      // }
      else if (this.currentActiveIndex == 3) {
        this.trackEventsInMixPanel([{ eventName: EVENTS.security_privacy_premium_keys_end }]);
      }
    } catch (error) {
      console.warn('🚀 ~ setStatusForSidenavMenu ~ error:', error);
    }
  }

  submitAllFormAnswers(value) {
    this.markAsComplete = !!value;
    if (!!value) {
      this.completeSetup();
    }
  }
  gotoPrevious() {
    if (this.currentActiveIndex > 0) {
      // if (this.currentActiveIndex === 3 && this.currentViewOnStep3 === 'premium-keys') {
      //   this.currentViewOnStep3 = 'about';
      //   this.setStatusForSidenavMenu(3);
      // } else {
      if (this.steps[this.currentActiveIndex - 1].disabled) {
        this.currentActiveIndex = this.currentActiveIndex - 2;
      } else {
        this.currentActiveIndex = this.currentActiveIndex - 1;
      }
      for (let i = 0; i < this.steps.length; i++) {
        if (i === this.currentActiveIndex) {
          this.steps[i].status = 'active';
          this.currentActiveElement = this.steps[i];
        } else {
          this.steps[i].status = 'inactive';
        }
      }
      // }
      this.cd.detectChanges();
    }
  }
  handleOptionValueEmitter(value) {
    if (value == 'next') this.handleSidenavMenuNavigation();
  }
  completeSetup() {
    this.subscription.add(
      this.userService
        .updateSecurityAndPrivacy({
          security_and_privacy_complete: this.markAsComplete,
        })
        .subscribe(() => {
          this.store.dispatch(new loginStoreActions.LoginRequestAction());
          this.closeSideNav();
        })
    );
  }
  searchVisibilityForm: UntypedFormGroup;

  onChangeSearch(value, search_type) {
    const { checked } = value;
    this.searchVisibilityForm.controls[search_type].setValue(checked);
    const dataObj = {};
    dataObj[search_type] = this.searchVisibilityForm.controls[search_type].value;
    this.subscription.add(
      this.userService.updateUser(dataObj).subscribe((res) => {
        this.store.dispatch(new loginStoreActions.LoginRequestAction());
        this.toaster.success('Search preferences have been saved');
        this.trackEventsInMixPanel([{ eventName: EVENTS.security_privacy_search_visibility_change }]);
      })
    );
    this.cd.detectChanges();
  }
  currentViewOnStep3 = 'premium-keys';
  premiumKeysEmitter(view: 'about' | 'premium-keys') {
    this.currentViewOnStep3 = view;
  }
}
